<template>
  <div v-if="!editing" class="bm-status">
    {{ selectedText }}
    <bm-btn icon small button-type="ghost" @click="editing = !editing"
      ><v-icon>edit</v-icon></bm-btn
    >
  </div>
  <div v-else>
    <v-select
      v-model="keyValue"
      :items="items"
      :item-text="itemText"
      :item-value="itemValueStringWrapper"
      class="d-inline-block bm-select"
    ></v-select>
    <bm-btn icon small button-type="ghost" @click="accept">
      <v-icon> {{ $vuetify.icons.values[icons.accept] }}</v-icon>
    </bm-btn>
    <bm-btn icon small button-type="ghost" @click="cancel">
      <v-icon> {{ $vuetify.icons.values[icons.cancel] }}</v-icon>
    </bm-btn>
  </div>
</template>

<script lang="ts">
import type { PropType } from "vue";
import { defineComponent } from "vue";
import { useThemedIcons } from "@/plugins/vuetify";
/**
 * A component that shows a status with an edit button. When clicked, turns into a dropdown to change the value.
 *
 * Values/Keys must be convertible to strings using the global 'String' constructor (e.g. all primitives or objects that
 * implement toString()).
 */
export default defineComponent({
  name: "BmPageStatusSelect",
  props: {
    value: {
      type: Object as PropType<any>,
      required: true,
    },
    items: {
      type: Array as PropType<any[]>,
      default: () => [],
    },
    itemValue: {
      type: Function as PropType<(item: any) => any>,
      default: () => () => "value",
    },
    itemText: {
      type: Function as PropType<(item: any) => string>,
      default: () => () => "text",
    },
  },
  setup() {
    const { icons } = useThemedIcons();
    return {
      icons,
    };
  },
  data() {
    return {
      editing: false,
      selectedItem: null as any | null,
      keyValue: null as any | null,
    };
  },
  computed: {
    itemValueStringWrapper(): (item: any) => string {
      return (item: any): string => {
        return String(this.itemValue(item));
      };
    },
    selectedText(): string {
      if (this.selectedItem) {
        return this.itemText(this.selectedItem);
      }
      if (this.value) {
        return this.itemText(this.value);
      } else return "";
    },
  },
  watch: {
    value: {
      // @ts-ignore
      handler: "valueWatch",
      immediate: true,
    },
    keyValue: {
      // @ts-ignore
      handler: "keyValueWatch",
      immediate: true,
    },
  },
  methods: {
    valueWatch(newValue: any) {
      this.keyValue = this.itemValueStringWrapper(this.value);
    },
    keyValueWatch() {
      this.selectedItem = this.getItemFromKey(this.keyValue);
    },
    getItemFromKey(key: any) {
      const foundItem = this.items.find(
        (i) => this.itemValueStringWrapper(i) === key
      );
      if (foundItem) {
        return foundItem;
      } else {
        return null;
      }
    },
    accept() {
      this.$emit("input", this.selectedItem);
      this.editing = false;
    },
    cancel() {
      this.selectedItem = null;
      this.$emit("input", this.value);
      this.editing = false;
    },
  },
});
</script>

<style scoped lang="sass">
// Make the select as wide as the longest item (otherwise it's an arbitrary 230px or so)
// https://github.com/vuetifyjs/vuetify/issues/6275
:deep(input)
  display: none

// Set the readonly height to be the same height as the text field to prevent the control from changing height while
// editing.
.bm-status
  min-height: 70px
</style>
